import { Dispatch, FC, SetStateAction, useState, useEffect, useMemo } from "react"

import { Button } from "@/components/ui/button" 
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { EmailChipsInput } from "@/components/ui/email-chips-input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { timezoneOptions } from "@/lib/mock-data"
import { Calendar, Clock, Loader2 } from "lucide-react"
import { Reminder, TemplateConfiguration, ReminderConfiguration, EditReminderForm } from "@/models/interface"
import { useSubjectSelector } from "@/common"
import { notificationStore } from "@/store/notification-store"
import { BaseTemplateTags, EditMessageContext, NotificationEntityType, RecipientType, ReminderRecipientGroupCodes, ReminderTriggerPeriod, ReminderType } from "@/models/enum"
import { useForm, Controller, FormProvider } from "react-hook-form"
import { useNotificationCenterApi } from "@/hooks"
import { applySupportEmail, applySupportEmailPlaceholder, generateTimeOptions, processTemplateContent } from "@/lib/utils"
import useTranslation from "@/hooks/use-translation"
import { 
    getDefaultReminderValues, 
    getFinalRecipientCodes,
    getInternalRecipientCheckboxOptions,
    normalizeEntityTypes,
    getReminderCategoryFromEntityTypes,
    getDefaultTriggerType
} from "@/lib/reminder-field-mapping"
import { toast } from "@/hooks/use-toast"

interface EditReminderProps {
    open: boolean
    onOpenChange: Dispatch<SetStateAction<boolean>>
    editingReminderConfig: ReminderConfiguration | null
    setEditingReminderConfig: Dispatch<SetStateAction<ReminderConfiguration | null>>
    remindersLoading: boolean
    onEditMessage: (template: TemplateConfiguration, context: EditMessageContext) => void
    onSaveSuccess?: () => void
}

export const EditReminderDialog: FC<EditReminderProps> = ({ 
    open: editReminderOpen, 
    onOpenChange: setEditReminderOpen, 
    editingReminderConfig,
    setEditingReminderConfig,
    remindersLoading,
    onEditMessage,
    onSaveSuccess
}) => {
    const { t, language } = useTranslation()
    const [withSampleData, setWithSampleData] = useState(true)
    const [templatesLoading, setTemplatesLoading] = useState(false)

    const { selectedRecipientType, templates, baseTemplate, newlyCreatedTemplateId, feedbackEmailAddress } = useSubjectSelector(notificationStore, [
        'selectedRecipientType', 'templates', 'baseTemplate', 'newlyCreatedTemplateId', 'feedbackEmailAddress'
    ])
    const isWorkerCategory = selectedRecipientType === RecipientType.WORKER
    const isInternalUsersCategory = selectedRecipientType === RecipientType.INTERNAL

    // Helper function to get English template content (index 0)
    const getEnglishTemplate = (template: TemplateConfiguration) => {
        return template.Data?.EmailTemplates?.[0]
    }

    const { fetchTemplateConfigurations, updateReminderConfiguration, fetchFeedbackEmailAddress } = useNotificationCenterApi()

    // Generate time options with 1 Hour intervals
    const timeOptions = generateTimeOptions()

    // Form setup
    const formInstance = useForm<EditReminderForm>({
        defaultValues: (() => {
            const entityTypes = isInternalUsersCategory ? [NotificationEntityType.TIMESHEET_EXPENSE] : [NotificationEntityType.TIMESHEET]
            const defaultCategory = getDefaultTriggerType(selectedRecipientType || "")
            const defaults = getDefaultReminderValues(selectedRecipientType || "", entityTypes, defaultCategory)
            
            return {
                ReminderTitle: "",
                ReminderEnabled: true,
                EntityTypes: entityTypes,
                RecipientType: selectedRecipientType || "",
                ReminderCategory: defaults.reminderCategory,
                RegularReminder: {
                    RecipientGroupCodes: isWorkerCategory 
                        ? [ReminderRecipientGroupCodes.OnlineTimesheetWorkers, 
                            ReminderRecipientGroupCodes.OfflineTimesheetWorks, 
                            ReminderRecipientGroupCodes.VmsTimesheetWorkers] 
                        : defaults.recipientCodes,
                    AdditionalRecipientEmails: [],
                    ReminderTemplateId: "",
                    ReminderTrigger: {
                        NumberOfUnit: 1,
                        UnitType: "BusinessDay",
                        TriggerType: defaults.triggerType || (isWorkerCategory ? ReminderTriggerPeriod.BeforePeriodEnds 
                            : ReminderTriggerPeriod.AfterSubmission),
                        Time: "09:00",
                        TimeZone: "America/New_York"
                    }
                },
                // Internal recipient fields
                InternalRecipientCheckboxes: defaults.internalRecipientCheckboxes,
            }
        })()
    })

    const { handleSubmit, register, watch, setValue, control, reset } = formInstance
    const selectedTemplateId = watch('RegularReminder.ReminderTemplateId')
    const selectedWhat = watch('EntityTypes.0')
    const reminderTitle = watch('ReminderTitle')
    const selectedWorkerGroupCodes = watch('RegularReminder.RecipientGroupCodes')
    const internalRecipientCheckboxes = watch('InternalRecipientCheckboxes')
    const numberOfUnits = watch('RegularReminder.ReminderTrigger.NumberOfUnit')

    // Comprehensive form validation for all visible UI fields
    const isFormValid: boolean = useMemo((): boolean => {
        // Always required fields
        if (!reminderTitle?.trim()) return false
        if (!selectedTemplateId) return false
        if(Number.isNaN(numberOfUnits)) return false

        // For non-worker categories (clients/internal), entity type selection is required
        if (!isWorkerCategory && !selectedWhat) return false

        // For workers, at least one recipient group code is required
        if (isWorkerCategory && (!selectedWorkerGroupCodes || selectedWorkerGroupCodes.length === 0)) return false

        // For internal users, at least one checkbox must be checked
        if (isInternalUsersCategory) {
            const hasCheckedRecipient = internalRecipientCheckboxes && 
                Object.values(internalRecipientCheckboxes).some(checked => checked === true)
            if (!hasCheckedRecipient) return false
        }

        return true
    }, [reminderTitle, 
        selectedTemplateId, 
        isWorkerCategory, 
        selectedWhat, 
        selectedWorkerGroupCodes, 
        isInternalUsersCategory,
        numberOfUnits,
        JSON.stringify(internalRecipientCheckboxes)])

    const isSubmitDisabled = !isFormValid || remindersLoading

    // Listen for newly created templates and switch to them
    useEffect(() => {
        if (newlyCreatedTemplateId && editReminderOpen) {
            setValue('RegularReminder.ReminderTemplateId', newlyCreatedTemplateId, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
            })
        }
    }, [newlyCreatedTemplateId, editReminderOpen, setValue])

    // Clear newly created template ID when dialog is closed
    useEffect(() => {
        if (!editReminderOpen && newlyCreatedTemplateId) {
            notificationStore.clearNewlyCreatedTemplateId()
        }
    }, [editReminderOpen, newlyCreatedTemplateId])

    // Initialize form with editing reminder data
    useEffect(() => {
        if (editReminderOpen && editingReminderConfig) {
            const editingReminder = editingReminderConfig.Data
            // Map from Reminder interface to form structure
            const originalEntityTypes = editingReminder.EntityTypes || [NotificationEntityType.TIMESHEET]
            
            // Reverse logic: if we have both TIMESHEET and EXPENSE, convert to TIMESHEET_EXPENSE
            const entityTypes = originalEntityTypes.includes(NotificationEntityType.TIMESHEET) && 
                              originalEntityTypes.includes(NotificationEntityType.EXPENSE_CLAIM)
                ? [NotificationEntityType.TIMESHEET_EXPENSE]
                : originalEntityTypes
            
            // For Internal recipients, set up checkbox states from existing data
            let internalRecipientCheckboxes: { [key: string]: boolean } = {}
            if (editingReminder.RecipientType === RecipientType.INTERNAL) {
                const existingCodes = editingReminder.RegularReminder?.RecipientGroupCodes || []
                const checkboxOptions = getInternalRecipientCheckboxOptions()
                checkboxOptions.forEach(option => {
                    internalRecipientCheckboxes[option.value] = existingCodes.includes(option.value)
                })
            }
            
            reset({
                ReminderTitle: editingReminder.ReminderTitle || "",
                ReminderEnabled: editingReminder.ReminderEnabled ?? true,
                EntityTypes: entityTypes,
                RecipientType: editingReminder.RecipientType || selectedRecipientType || "",
                ReminderCategory: editingReminder.ReminderCategory,
                RegularReminder: {
                    RecipientGroupCodes: editingReminder.RegularReminder?.RecipientGroupCodes || [],
                    AdditionalRecipientEmails: editingReminder.RegularReminder?.AdditionalRecipientEmails || [],
                    ReminderTemplateId: editingReminder.RegularReminder?.ReminderTemplateId || "",
                    ReminderTrigger: {
                        NumberOfUnit: editingReminder.RegularReminder?.ReminderTrigger?.NumberOfUnit,
                        UnitType: editingReminder.RegularReminder?.ReminderTrigger?.UnitType || "BusinessDay",
                        TriggerType: editingReminder.RegularReminder?.ReminderTrigger?.TriggerType || ReminderTriggerPeriod.BeforePeriodEnds,
                        Time: editingReminder.RegularReminder?.ReminderTrigger?.Time || "09:00",
                        TimeZone: editingReminder.RegularReminder?.ReminderTrigger?.TimeZone || "America/New_York"
                    }
                },
                // Internal recipient fields
                InternalRecipientCheckboxes: internalRecipientCheckboxes,
            })
            setWithSampleData(true)
            
            // Normalize trigger type for internal users with expense claims
            const editingRecipientType = editingReminder.RecipientType
            const selectedWhat = (entityTypes ?? [])[0]
            const isInternalExpense = editingRecipientType === RecipientType.INTERNAL 
                && selectedWhat === NotificationEntityType.EXPENSE_CLAIM

            if (isInternalExpense) {
                setValue('RegularReminder.ReminderTrigger.TriggerType', ReminderTriggerPeriod.AfterSubmission, {
                    shouldValidate: true, shouldDirty: true, shouldTouch: true
                })
            }
        }
    }, [editReminderOpen, editingReminderConfig, selectedRecipientType, reset])

    const getAvailableTemplatesForEditReminder = (reminderType: ReminderType, entityTypes: string[]): TemplateConfiguration[] => {
        // Default to timesheet for workers if no entity types specified
        let effectiveEntityTypes = entityTypes
        if ((!effectiveEntityTypes || effectiveEntityTypes.length === 0) && isWorkerCategory) {
            effectiveEntityTypes = [NotificationEntityType.TIMESHEET]
        }
        
        // Convert TIMESHEET_EXPENSE to actual entity types array
        const expandedEntityTypes: string[] = []
        effectiveEntityTypes.forEach((entityType: string) => {
            if (entityType === NotificationEntityType.TIMESHEET_EXPENSE) {
                // TIMESHEET_EXPENSE means both timesheet and expense
                expandedEntityTypes.push(NotificationEntityType.TIMESHEET)
                expandedEntityTypes.push(NotificationEntityType.EXPENSE_CLAIM)
            } else {
                expandedEntityTypes.push(entityType)
            }
        })
        
        const recipientType = watch('RecipientType') || selectedRecipientType || ""
        const reminderCategory = getReminderCategoryFromEntityTypes(recipientType, expandedEntityTypes, watch('RegularReminder.ReminderTrigger.TriggerType')) 
        return templates
            .filter(template =>
                template?.Data?.RecipientType === selectedRecipientType
                && template?.Data?.ReminderCategories?.includes(reminderCategory)
                && template.Data.TemplateType === reminderType)
    }

    // Load templates from API
    useEffect(() => {
        if (editReminderOpen && editingReminderConfig) {
            setTemplatesLoading(true)
            const initialTemplateId = editingReminderConfig.Data.RegularReminder.ReminderTemplateId
            fetchFeedbackEmailAddress()
            fetchTemplateConfigurations(initialTemplateId) // Pass initial template ID for edit mode
                .then(() => {
                    // Templates are automatically updated in notification store
                })
                .catch((error) => {
                    console.error('Error fetching templates:', error)
                })
                .finally(() => {
                    setTemplatesLoading(false)
                })
        }
    }, [editReminderOpen, editingReminderConfig])

    // Ensure template selections are preserved after templates load
    useEffect(() => {
        if (!templatesLoading && templates.length > 0 && editingReminderConfig && editReminderOpen && !newlyCreatedTemplateId) {
            const editingReminder = editingReminderConfig.Data
          // Verify and maintain the regular template selection
            const regularTemplateId = editingReminder.RegularReminder?.ReminderTemplateId
            if (regularTemplateId) {
                const templateExists = templates.some(t => t.id === regularTemplateId)
                if (templateExists) {
                    setValue('RegularReminder.ReminderTemplateId', regularTemplateId)
                } else {
                    console.warn(`Regular template ${regularTemplateId} not found in available templates`)
                }
            }
        }
    }, [templatesLoading, templates, editingReminderConfig, editReminderOpen, setValue, newlyCreatedTemplateId])

    const saveReminderChanges = async (data: EditReminderForm) => {
        if (!editingReminderConfig) return
        
        try {
            // Transform form data back to Reminder interface
            const entityTypes = normalizeEntityTypes(data.EntityTypes)

            // Get recipient codes based on category and entity types
            let recipientGroupCodes: string[] = []
            
            if (isWorkerCategory) {
                // For workers, use the selected recipient codes
                recipientGroupCodes = getFinalRecipientCodes(
                    data.RecipientType, 
                    data.EntityTypes, 
                    data.RegularReminder.RecipientGroupCodes
                )
            } else if (isInternalUsersCategory) {
                // For internal, collect checked recipients
                const selectedCheckboxes = Object.entries(data.InternalRecipientCheckboxes || {})
                    .filter(([_, checked]) => checked)
                    .map(([code, _]) => code)
                
                recipientGroupCodes = getFinalRecipientCodes(
                    data.RecipientType,
                    data.EntityTypes,
                    selectedCheckboxes
                )
            } else {
                // For clients, determine automatically based on entity types
                recipientGroupCodes = getFinalRecipientCodes(
                    data.RecipientType,
                    data.EntityTypes,
                    []
                )
            }

            // Belt-and-suspenders: Enforce trigger type for internal expense claims
            const selectedWhat = data.EntityTypes?.[0]
            const isInternalExpense = data.RecipientType === RecipientType.INTERNAL 
                && selectedWhat === NotificationEntityType.EXPENSE_CLAIM
            const effectiveTriggerType = isInternalExpense 
                ? ReminderTriggerPeriod.AfterSubmission 
                : data?.RegularReminder?.ReminderTrigger?.TriggerType

            const updatedReminder: Reminder = {
                ...editingReminderConfig.Data,
                ReminderTitle: data.ReminderTitle,
                ReminderEnabled: data.ReminderEnabled,
                EntityTypes: entityTypes, 
                RecipientType: data.RecipientType,
                ReminderCategory: getReminderCategoryFromEntityTypes(data.RecipientType, data.EntityTypes, effectiveTriggerType),
                RegularReminder: {
                    ...data.RegularReminder,
                    RecipientGroupCodes: recipientGroupCodes,
                    ReminderTrigger: {
                        ...data.RegularReminder.ReminderTrigger,
                        TriggerType: effectiveTriggerType
                    }
                }
            }

            // Create updated reminder configuration
            const updatedReminderConfig: ReminderConfiguration = {
                ...editingReminderConfig,
                Data: updatedReminder
            }

            // Save via API
            await updateReminderConfiguration(updatedReminderConfig)
            
            // Close dialog and notify parent of success
            setEditReminderOpen(false)
            setEditingReminderConfig(null)
            
            // Trigger refresh in parent component
            if (onSaveSuccess) {
                onSaveSuccess()
            }
        } catch (error) {
            toast({
                description: 'Error saving reminder',
                variant: "destructive"
            })
            // Handle error (could show toast notification here)
        }
    }

    const baseTemplateStructure = baseTemplate ? getEnglishTemplate(baseTemplate)?.Body : ""
    const templateId = selectedTemplateId
    const template = templates.find(t => t.id === templateId)
    
    let subject = ""
    let content = ""
    let injectedContent = ""
    
    if (template) {
        const englishTemplate = getEnglishTemplate(template)
        const rawSubject = englishTemplate.Subject ?? ""
        const rawContent = englishTemplate.Body ?? ""
        
        // Process template content with meta tags
        const processed = processTemplateContent(template, rawSubject, rawContent, withSampleData, language)
        subject = processed.subject
        content = processed.content
        
        // Chrome first, then content — the same order the processor sends in, so tenant-authored content
        // mentioning the platform address previews exactly as it will be delivered.
        injectedContent = applySupportEmailPlaceholder(
            applySupportEmail(baseTemplateStructure ?? "", feedbackEmailAddress)
            .replace(BaseTemplateTags.content, content)
            .replace(BaseTemplateTags.subject, subject), feedbackEmailAddress)
    }
    
    return (
        <Dialog open={editReminderOpen} onOpenChange={setEditReminderOpen}>
          <DialogContent className="max-w-7xl">
            <FormProvider {...formInstance}>
              <form onSubmit={handleSubmit(saveReminderChanges)}>
                <div className="h-[90vh] overflow-hidden p-0 flex flex-col">
                  <DialogHeader className="px-6 py-4 border-b">
                    <DialogTitle>{t("dashboard.editReminder.title")}</DialogTitle>
                  </DialogHeader>

                  {editingReminderConfig && (
                    <div className="flex flex-1 overflow-hidden">
                      {/* Left Side - Form */}
                      <div className="w-3/6 p-6 overflow-y-auto border-r">
                        <div className="space-y-6">
                          
                          {/* Title */}
                          <div className="space-y-2">
                            <Label htmlFor="reminder-title">
                                {t("dashboard.editReminder.titleLabel")}
                                <span className="text-sm text-red-500 ml-1">*</span>
                            </Label>
                            <Input
                                id="reminder-title"
                                {...register("ReminderTitle")}
                                placeholder={t("dashboard.editReminder.titlePlaceholder")}
                            />
                          </div>

                          {/* What - Show for clients and internal users */}
                          {!isWorkerCategory && (
                              <div className="space-y-2">
                                <Label htmlFor="reminder-what">{t("dashboard.editReminder.whatLabel")}</Label>
                                <Controller
                                    name="EntityTypes.0"
                                    control={control}
                                    render={({ field }) => (
                                        <Select value={field.value || ''} onValueChange={(value) => {
                                        field.onChange(value)
                                        setValue('EntityTypes', [value])
                                        // Reset template selections when entity type changes
                                        setValue('RegularReminder.ReminderTemplateId', '')
                                        // For internal users, set trigger type based on entity type
                                        if (isInternalUsersCategory && value === NotificationEntityType.EXPENSE_CLAIM) {
                                            setValue('RegularReminder.ReminderTrigger.TriggerType', ReminderTriggerPeriod.AfterSubmission)
                                        }
                                    }}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={NotificationEntityType.TIMESHEET}>{t("dashboard.editReminder.timesheets")}</SelectItem>
                                                <SelectItem value={NotificationEntityType.EXPENSE_CLAIM}>{t("dashboard.editReminder.expenses")}</SelectItem>
                                                <SelectItem value={NotificationEntityType.TIMESHEET_EXPENSE}>{t("dashboard.editReminder.timesheetsAndExpenses")}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                              </div>
                          )}

                          {/* Who - Different logic for workers vs clients vs internal */}
                          {selectedRecipientType === RecipientType.WORKER && (
                              <div className="space-y-2">
                                <Label htmlFor="reminder-who">
                                    {t("dashboard.editReminder.whoLabel")}
                                    <span className="text-sm text-red-500 ml-1">*</span>
                                </Label>
                                <Controller
                                    name="RegularReminder.RecipientGroupCodes"
                                    control={control}
                                    render={({ field }) => {
                                        // Determine current display value based on RecipientGroupCodes array
                                        const currentCodes = field.value || []
                                        const hasOffline = currentCodes.includes(ReminderRecipientGroupCodes.OfflineTimesheetWorks)
                                        const hasOnline = currentCodes.includes(ReminderRecipientGroupCodes.OnlineTimesheetWorkers)
                                        const hasVms = currentCodes.includes(ReminderRecipientGroupCodes.VmsTimesheetWorkers)
                                        
                                        let displayValue: string
                                        if (hasOffline && hasOnline && hasVms) {
                                            displayValue = ReminderRecipientGroupCodes.AllWorkers
                                        } else if (hasOnline && !hasOffline && !hasVms) {
                                            displayValue = ReminderRecipientGroupCodes.OnlineTimesheetWorkers
                                        } else if (hasOffline && !hasOnline && !hasVms) {
                                            displayValue = ReminderRecipientGroupCodes.OfflineTimesheetWorks
                                        } else if (hasVms && !hasOnline && !hasOffline) {
                                            displayValue = ReminderRecipientGroupCodes.VmsTimesheetWorkers
                                        } else {
                                            displayValue = ReminderRecipientGroupCodes.AllWorkers
                                        }
                                        
                                        return (
                                            <Select value={displayValue} onValueChange={(value) => {
                                                if (value === ReminderRecipientGroupCodes.AllWorkers) {
                                                    const allWorkerCodes = [
                                                        ReminderRecipientGroupCodes.OnlineTimesheetWorkers,
                                                        ReminderRecipientGroupCodes.OfflineTimesheetWorks,
                                                        ReminderRecipientGroupCodes.VmsTimesheetWorkers
                                                    ]
                                                    field.onChange(allWorkerCodes)
                                                    setValue('RegularReminder.RecipientGroupCodes', allWorkerCodes)
                                                } else {
                                                    field.onChange([value])
                                                    setValue('RegularReminder.RecipientGroupCodes', [value])
                                                }
                                            }}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value={ReminderRecipientGroupCodes.AllWorkers}>
                                                        <span>{t("dashboard.editReminder.allWorkers")}</span>
                                                    </SelectItem>
                                                    <SelectItem value={ReminderRecipientGroupCodes.OnlineTimesheetWorkers}>
                                                        <span>{t("dashboard.editReminder.workersOnlineTimesheets")}</span>
                                                    </SelectItem>
                                                    <SelectItem value={ReminderRecipientGroupCodes.OfflineTimesheetWorks}>
                                                        <span>{t("dashboard.editReminder.workersOfflineTimesheets")}</span>
                                                    </SelectItem>
                                                    <SelectItem value={ReminderRecipientGroupCodes.VmsTimesheetWorkers}>
                                                        <span>{t("dashboard.editReminder.workersVmsTimesheets")}</span>
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )
                                    }}
                                />
                              </div>
                          )}

                          {/* Internal Recipients - Checkboxes and additional email */}
                          {isInternalUsersCategory && (
                              <div className="space-y-3">
                                  <Label htmlFor="edit-reminder-who">
                                    {t("dashboard.editReminder.whoLabel")}
                                    <span className="text-sm text-red-500 ml-1">*</span>
                                </Label>
                                  
                                  {/* Checkboxes for Internal recipients */}
                                  <div className="space-y-2">
                                      {getInternalRecipientCheckboxOptions().map((option) => (
                                          <div key={option.value} className="flex items-center space-x-2">
                                              <Controller
                                                  name={`InternalRecipientCheckboxes.${option.value}` as any}
                                                  control={control}
                                                  render={({ field }) => (
                                                      <Checkbox
                                                          id={`internal-recipient-${option.value}`}
                                                          checked={field.value || false}
                                                          onCheckedChange={(checked) => field.onChange(checked)}
                                                      />
                                                  )}
                                              />
                                              <Label 
                                                  htmlFor={`internal-recipient-${option.value}`} 
                                                  className="text-sm font-normal cursor-pointer"
                                              >
                                                  {option.label}
                                              </Label>
                                          </div>
                                      ))}
                                  </div>

                                  {/* Additional Email Input */}
                                  <div className="space-y-2">
                                      <Controller
                                          name="RegularReminder.AdditionalRecipientEmails"
                                          control={control}
                                          render={({ field }) => (
                                              <EmailChipsInput
                                                  id="internal-additional-emails"
                                                  label={t("dashboard.headings.additionalEmail")}
                                                  placeholder={t("dashboard.headings.additionalEmailPlaceholder")}
                                                  value={field.value || []}
                                                  onChange={field.onChange}
                                                  maxEmails={5}
                                              />
                                          )}
                                      />
                                  </div>
                              </div>
                          )}

                          {/* When - Different behavior for workers vs clients vs internal */}
                          <div className="space-y-3">
                            <Label>{t("dashboard.editReminder.whenLabel")}</Label>
                            
                            {/* Internal Users - Multi-line format with different arrangement */}
                            {isInternalUsersCategory ? (
                                <>
                                    <div className="flex items-center gap-2">
                                        {selectedWhat === NotificationEntityType.EXPENSE_CLAIM ? (
                                            // Show only text for Expense Claims
                                            <span className="text-sm text-gray-700">
                                                {t("dashboard.trigger.waitingForApproval")}
                                            </span>
                                        ) : (
                                            // Show dropdown for other entity types
                                            <Controller
                                                name="RegularReminder.ReminderTrigger.TriggerType"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select value={field.value || ""} onValueChange={(value) => {
                                                        field.onChange(value)
                                                        // Reset template selection when trigger type changes for Internal users
                                                        // because different trigger types have different available templates
                                                        setValue('RegularReminder.ReminderTemplateId', '')
                                                    }}>
                                                        <SelectTrigger className="flex-1">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value={ReminderTriggerPeriod.AfterPeriodEnds}>
                                                                {t("dashboard.trigger.waitingForSubmission")}
                                                            </SelectItem>
                                                            <SelectItem value={ReminderTriggerPeriod.AfterSubmission}>
                                                                {t("dashboard.trigger.waitingForApproval")}
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            />
                                        )}
                                        <span className="text-sm text-gray-700">for</span>
                                        <span className="text-sm text-red-500">*</span>
                                        <Input
                                            type="number"
                                            min="0"
                                            {...register("RegularReminder.ReminderTrigger.NumberOfUnit", { valueAsNumber: true })}
                                            className="min-w-11 max-w-14 no-spinners"
                                        />
                                        <span className="text-sm text-gray-700">business days,</span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-700">send at</span>
                                        <Controller
                                            name="RegularReminder.ReminderTrigger.Time"
                                            control={control}
                                            render={({ field }) => (
                                                <Select value={field.value} onValueChange={field.onChange}>
                                                    <SelectTrigger className="w-28">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="h-40 overflow-y-auto">
                                                        {timeOptions.map((time) => (
                                                            <SelectItem key={time.value} value={time.value}>
                                                                {time.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                        <Controller
                                            name="RegularReminder.ReminderTrigger.TimeZone"
                                            control={control}
                                            render={({ field }) => (
                                                <Select value={field.value} onValueChange={field.onChange}>
                                                    <SelectTrigger className="flex-1">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="max-h-[300px] overflow-y-auto">
                                                        {timezoneOptions.map((tz) => (
                                                            <SelectItem key={tz.value} value={tz.value}>
                                                                {tz.label} ({tz.offset})
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                    </div>
                                </>
                            ) : (
                                /* Workers and Clients - Original layout */
                                <>
                                    <div className="flex items-center gap-2">
                                      <Calendar className="w-4 h-4 text-gray-500" />
                                      <span className="text-sm text-red-500">*</span>
                                      <Input
                                          type="number"
                                          min="0"
                                          {...register("RegularReminder.ReminderTrigger.NumberOfUnit", { valueAsNumber: true })}
                                          className="min-w-11 max-w-14 no-spinners"
                                      />
                                      {
                                        isWorkerCategory ? 
                                            <span className="text-sm text-gray-500">{t("dashboard.editReminder.businessDays")} </span>
                                            :
                                            <span className="text-sm text-gray-500">{t("dashboard.editReminder.businessDaysComma")} </span>
                                      }
                                      {isWorkerCategory ? (
                                          <Controller
                                              name="RegularReminder.ReminderTrigger.TriggerType"
                                              control={control}
                                              render={({ field }) => (
                                                  <Select value={field.value || ""} onValueChange={field.onChange}>
                                                      <SelectTrigger className="flex-1">
                                                          <SelectValue />
                                                      </SelectTrigger>
                                                      <SelectContent>
                                                          <SelectItem value={ReminderTriggerPeriod.BeforePeriodEnds}>
                                                              {t("dashboard.editReminder.beforePeriodEnds")}
                                                          </SelectItem>
                                                          <SelectItem value={ReminderTriggerPeriod.AfterPeriodEnds}>
                                                              {t("dashboard.editReminder.afterPeriodEnds")}
                                                          </SelectItem>
                                                      </SelectContent>
                                                  </Select>
                                              )}
                                          />
                                      ) : (
                                          <span className="text-sm text-gray-500 -ml-1">{t("dashboard.editReminder.afterSubmission")}</span>
                                      )}
                                    </div>

                                    <div className="flex items-center gap-2">
                                      <Clock className="w-4 h-4 text-gray-500" />
                                      <Controller
                                          name="RegularReminder.ReminderTrigger.Time"
                                          control={control}
                                          render={({ field }) => (
                                              <Select value={field.value} onValueChange={field.onChange}>
                                                  <SelectTrigger className="w-32">
                                                      <SelectValue />
                                                  </SelectTrigger>
                                                  <SelectContent className="h-40 overflow-y-auto">
                                                      {timeOptions.map((time) => (
                                                          <SelectItem key={time.value} value={time.value}>
                                                              {time.label}
                                                          </SelectItem>
                                                      ))}
                                                  </SelectContent>
                                              </Select>
                                          )}
                                      />
                                      <Controller
                                          name="RegularReminder.ReminderTrigger.TimeZone"
                                          control={control}
                                          render={({ field }) => (
                                              <Select value={field.value} onValueChange={field.onChange}>
                                                  <SelectTrigger className="flex-1">
                                                      <SelectValue />
                                                  </SelectTrigger>
                                                  <SelectContent className="max-h-[300px] overflow-y-auto">
                                                      {timezoneOptions.map((tz) => (
                                                          <SelectItem key={tz.value} value={tz.value}>
                                                              {tz.label} ({tz.offset})
                                                          </SelectItem>
                                                      ))}
                                                  </SelectContent>
                                              </Select>
                                          )}
                                      />
                                    </div>
                                </>
                            )}

                            <div className="flex items-center gap-2">
                                <ul className="list-disc list-inside">
                                    <li className="text-xs text-gray-500">
                                        {t("dashboard.alerts.reminderSentDaily1")}
                                    </li>
                                    <li className="text-xs text-gray-500 mt-1">
                                        {
                                            isWorkerCategory ? t("dashboard.alerts.reminderSentDailySubmitted")
                                            : isInternalUsersCategory ? t("dashboard.alerts.reminderSentDailySubmittedAndApproved")
                                            : t("dashboard.alerts.reminderSentDailyApproved")
                                        }
                                    </li>
                                </ul>
                            </div>
                          </div>

                          {/* Template */}
                          <div className="space-y-2">
                            <Label htmlFor="reminder-template">{t("dashboard.editReminder.templateLabel")}</Label>
                            <Controller
                                name="RegularReminder.ReminderTemplateId"
                                control={control}
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange} disabled={templatesLoading}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={
                                                templatesLoading ? (
                                                    <span className="flex items-center gap-2">
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        {t("dashboard.editReminder.loadingTemplates")}
                                                    </span>
                                                ) : (
                                                    <span>
                                                        {t("dashboard.editReminder.selectTemplateRequired")} <span className="text-red-500">*</span>
                                                    </span>
                                                )
                                            } />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {getAvailableTemplatesForEditReminder(ReminderType.REGULAR, watch('EntityTypes')).map((template) => (
                                                <SelectItem key={template.id} value={template.id ?? ''}>
                                                    {`${template.Data.TemplateName}`}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                            {!templatesLoading && getAvailableTemplatesForEditReminder(ReminderType.REGULAR, watch('EntityTypes')).length === 0 && (
                                <p className="text-sm text-gray-500">
                                  {templates.length === 0 
                                      ? t("dashboard.editReminder.noTemplatesConfigured")
                                      : t("dashboard.editReminder.noTemplatesAvailable")
                                  }
                                </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right Side - Email Preview */}
                      <div className="w-4/6 bg-gray-50 overflow-y-auto flex flex-col">
                        {/* Preview Header */}
                        <div className="bg-white border-b px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <span className="text-gray-600 font-medium">{t("dashboard.editReminder.preview")}</span>
                            </div>

                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <Switch checked={withSampleData} onCheckedChange={setWithSampleData} />
                                <span className="text-sm text-gray-600">{t("dashboard.editReminder.withSampleData")}</span>
                              </div>
                              <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="text-blue-600 border-blue-200 hover:bg-blue-50 bg-transparent"
                                    onClick={() => {
                                        const templateId = selectedTemplateId
                                        const template = templates.find((t) => t.id === templateId)
                                        if (template) {
                                            onEditMessage(template, EditMessageContext.Edit)
                                        }
                                    }}
                                  disabled={!selectedTemplateId}
                              >
                                <span className="mr-1">✏️</span>
                                {t("dashboard.editReminder.edit")}
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Email Preview Content */}
                        <div className="flex-1 bg-gray-50 overflow-x-hidden">
                            <div dangerouslySetInnerHTML={{ __html: injectedContent ?? '' }} />
                        </div>
                        
                      </div>
                    </div>
                  )}

                  {/* Footer Actions - Always visible */}
                  <div className="flex justify-end items-center px-6 py-4 border-t bg-gray-50 flex-shrink-0">
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={() => setEditReminderOpen(false)}>
                        {t("dashboard.editReminder.cancel")}
                      </Button>
                      <Button
                          type="submit"
                          className="bg-slate-800 hover:bg-slate-700"
                          disabled={isSubmitDisabled}
                      >
                        {remindersLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        {t("dashboard.editReminder.saveReminder")}
                      </Button>
                    </div>
                  </div>
                </div>
              </form>
            </FormProvider>
          </DialogContent>
        </Dialog>
    )
}